import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Button, ButtonGroup, Container, Form,
} from 'react-bootstrap';
import ConversationList, { participantName } from './Components/ConversationList';
import ConversationThread from './Components/ConversationThread';
import ContextPanel from './Components/ContextPanel';
import EmailInboxService, { searchFilter, searchHint } from '../../Services/EmailInboxService';
import DeskService, {
  DEFAULT_VIEW,
  DESK_VIEWS,
  FILED_VIEW,
  deskErrorText,
  deskState,
  isHeldByAnother,
  resolveView,
  reviewFromError,
  viewKeeps,
  withReview,
} from '../../Services/CustomerServiceDeskService';
import UserService from '../../Services/UserService';
import { conversationBookingReference } from '../../Utils/bookingReference';
import { FILING_FILTERS, filingMatches } from '../../Utils/inboundTriage';

/**
 * CUSTOMER SERVICE — the desk's shared inbox, as a three-pane console.
 *
 * Left, the queue: what is waiting, who has it, where it arrived. Centre, the
 * conversation and the decision waiting on it. Right, everything about it
 * that is not in it — the booking, the person, their other mail, and what the
 * agent looked up before drafting. The bar it is built to is respond.io,
 * which is what the office uses all day for WhatsApp.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * NOTHING ON THIS SCREEN SENDS ANYTHING. APPROVING A DRAFT IS NOT A SEND.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * There is no outbound transport in this phase on either side of the wire —
 * the API's `DraftCheckNoSend` test walks the real import graph to hold that,
 * and no service this page imports has a send call. Approving records that a
 * human accepted a text; what happens to an accepted text is a later phase's
 * question, which is why there is no send button here, not even a disabled
 * one. See Components/DraftSuggestion.
 *
 * ── TWO HALVES, AND THE SCREEN SAYS WHEN EITHER IS MISSING ──
 * The ARCHIVE (`GET /email-inbox`) is what every conversation and message on
 * this screen comes from. The DESK — who holds a conversation, how it was
 * closed, the draft waiting on it, what triage decided — is `GET /ai/drafts`,
 * through `CustomerServiceDeskService`. They fail independently and each
 * failure has its own slot, because a queue drawn without desk state shows
 * every held conversation as free: the exact failure the holder is there to
 * prevent. It never degrades silently.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE DEFAULT VIEW IS WHAT TRIAGE PASSED, WHICH IS WHY THE QUEUE IS QUIET.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * "shouldn't no-reply and automated emails bypass through the analyzer ai? i
 * am seeing a lot showing up in CS" — they should, and the verdict already
 * existed; this screen simply never read it. `GET /email-inbox` filters by
 * mailbox and nothing else, so every newsletter the gates had already caught
 * was still in the list looking like unanswered customer mail.
 *
 * Three things follow, and all three are visible rather than silent:
 *
 *   A REFUSED CONVERSATION IS FILED, under its own reason, with a reason
 *   filter. "Why did nobody answer this" is one click. A gate whose output
 *   cannot be inspected looks exactly like a gate that is working while it
 *   eats enquiries.
 *
 *   AN UNTRIAGED CONVERSATION IS NEITHER. Triage is new and never runs
 *   backwards, so ~44,766 archived emails have no verdict; they are under
 *   `Not triaged` with the epoch printed, because a default view that was
 *   quietly empty would read as a broken filter and one that included them
 *   would be the screen being complained about.
 *
 *   A FAILED RUN IS NOT A REFUSAL. A Bedrock throttle leaves a conversation
 *   eligible with no draft — "we do not know yet" — and it stays in `Needs
 *   us` where a person sees it.
 *
 * AND THERE IS NO THIRD CLASSIFIER HERE. No sender list, no domain rule, no
 * heuristic of this screen's own. Two classifiers exist already and they
 * disagree in measured ways — over 294 recent objects the header gates caught
 * neither AWS SNS notifications nor Trustpilot, only the model did — so a
 * third in the browser would be the copy that drifts and the one nobody
 * remembers. The console renders the verdict; it never forms one.
 *
 * ── WHY THE VIEWS ARE NOT THE ARCHIVE'S THREE STATES ──
 * `replied_state` answers "did something go out", which is not a question a
 * desk asks. The desk asks "what needs us", "what is mine", "what has a draft
 * waiting", "what did we decide needed no answer". Those are
 * `DeskService.DESK_VIEWS`, and each one says which part of itself the server
 * can filter and which part only narrows what is loaded — see that list,
 * which is where the whole mapping lives so this file and the queue cannot
 * disagree about it.
 *
 * ── THE COUNT IS ON THE OPEN VIEW AND NOWHERE ELSE ──
 * A view with its own server filter is a different page of the archive, so a
 * count for "Mine" computed over the page "Needs us" fetched would be a
 * number that is quietly wrong. One rule instead: the open view carries the
 * count of what is loaded, and a `+` while another page remains. Which is
 * also the number that matters, because the view the console opens on is the
 * one the desk is measured by.
 */

/** The page size for the archive, and for the desk entries joined onto it. */
const PAGE = 50;
const DESK_PAGE = 200;

function CustomerService() {
  const [viewKey, setViewKey] = useState(DEFAULT_VIEW);
  const [filing, setFiling] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [conversations, setConversations] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [resolution, setResolution] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  // The desk's own rows for the window, by conversation id.
  const [entries, setEntries] = useState({});
  const [deskLoading, setDeskLoading] = useState(true);
  const [deskTruncated, setDeskTruncated] = useState(false);
  const [deskFailure, setDeskFailure] = useState('');
  const [triageEpoch, setTriageEpoch] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState('');

  // The open conversation's desk state in full: the entry, its draft, its run.
  const [open, setOpen] = useState({ entry: null, draft: null, run: null });
  const [busy, setBusy] = useState(false);
  const [deskError, setDeskError] = useState('');

  const me = UserService.getInstance().getEmail();
  const view = resolveView(viewKey);

  const filters = useMemo(() => ({
    ...view.server,
    dateFrom,
    dateTo,
    ...searchFilter(search),
  }), [view, dateFrom, dateTo, search]);

  /**
   * A sequence guard, the same idiom the payments booking lookup uses: two
   * view clicks in a row are two requests in flight, and the slower one must
   * not land last and paint the list it no longer matches.
   */
  const loadSeq = useRef(0);
  const deskSeq = useRef(0);

  /**
   * `from` is the cursor to continue at, and it is passed IN rather than read
   * from state: this callback is memoised on the filters, so a cursor closed
   * over here would still be the first page's on a second "Load older" and
   * would append the same rows again.
   */
  const load = useCallback(async ({ append = false, from = null } = {}) => {
    loadSeq.current += 1;
    const mine = loadSeq.current;
    if (append) setLoadingMore(true); else setListLoading(true);
    setError('');
    try {
      const params = { ...filters, limit: PAGE };
      if (append && from) params.cursor = from;
      const result = await EmailInboxService.listConversations(params);
      if (mine !== loadSeq.current) return;
      setConversations((prev) => (
        append ? [...prev, ...result.conversations] : result.conversations
      ));
      setCursor(result.cursor);
      setResolution(result.resolution);
    } catch (err) {
      if (mine !== loadSeq.current) return;
      setError(err?.response?.data?.message || 'Could not load conversations');
    } finally {
      if (mine === loadSeq.current) {
        setListLoading(false);
        setLoadingMore(false);
      }
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  /**
   * The desk's rows for the window.
   *
   * ONE REQUEST, NOT ONE PER ROW, and not keyed by the ids on screen — a
   * conversation id is a Message-ID or an S3 key and fifty of them do not fit
   * a query string worth sending. See the service, which is where that
   * reasoning lives.
   *
   * It is re-fetched on the DATE RANGE only, not on the view or the search: a
   * desk row is a desk row whatever the archive is being asked for, so
   * narrowing the list must not cost a second round trip.
   */
  const loadEntries = useCallback(async () => {
    deskSeq.current += 1;
    const mine = deskSeq.current;
    setDeskLoading(true);
    setDeskFailure('');
    try {
      // `from` / `to`, which is what `/ai/drafts` calls the same range over
      // the same `occurred_at` that `/ai/decisions` filters on. The archive
      // spells them `dateFrom` / `dateTo`; the translation is here, at the one
      // call site, rather than either endpoint growing a second pair.
      const result = await DeskService.listEntries({
        from: dateFrom, to: dateTo, limit: DESK_PAGE,
      });
      if (mine !== deskSeq.current) return;
      setEntries(Object.fromEntries(
        result.entries.map((entry) => [entry.conversation_id, entry]),
      ));
      setDeskTruncated(result.truncated);
      setTriageEpoch(result.triageEpoch);
    } catch (err) {
      if (mine !== deskSeq.current) return;
      setEntries({});
      // IN ITS OWN SLOT, not the archive's: the two halves fail
      // independently, and a desk failure cleared by somebody typing in the
      // search box would be a queue drawing every held conversation as free
      // with nothing on screen to say so.
      setDeskFailure(deskErrorText(err, 'Could not read the desk'));
    } finally {
      if (mine === deskSeq.current) setDeskLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  /** One conversation's messages, from the archive. */
  useEffect(() => {
    if (!selectedId) {
      setThread(null);
      return undefined;
    }
    let live = true;
    setThreadLoading(true);
    setThreadError('');
    EmailInboxService.getConversation(selectedId)
      .then((result) => {
        if (!live) return;
        setThread(result);
        if (!result) setThreadError('This conversation could not be read.');
      })
      .catch((err) => {
        if (live) setThreadError(err?.response?.data?.message || 'Could not load it');
      })
      .finally(() => {
        if (live) setThreadLoading(false);
      });
    return () => { live = false; };
  }, [selectedId]);

  /**
   * The same conversation's desk state in full — the authoritative read for
   * the pane that offers the controls.
   *
   * The queue's joined entry is good enough to draw a badge; this is what the
   * holder controls and the draft are drawn from, because by the time
   * somebody has opened a conversation the list behind them may be minutes
   * old and the one thing that must not be stale is who has it.
   */
  useEffect(() => {
    if (!selectedId) {
      setOpen({ entry: null, draft: null, run: null });
      return undefined;
    }
    let live = true;
    setDeskError('');
    DeskService.getConversation(selectedId)
      .then((result) => {
        if (live) setOpen(result);
      })
      .catch((err) => {
        if (!live) return;
        setOpen({ entry: null, draft: null, run: null });
        setDeskError(deskErrorText(err, 'Could not read this conversation’s desk state'));
      });
    return () => { live = false; };
  }, [selectedId]);

  /** The derived desk state of one conversation. One decision, one place. */
  const stateOf = useCallback(
    (conversation) => deskState(conversation, entries[conversation.id], me),
    [entries, me],
  );

  /**
   * What this view keeps of the page that came back.
   *
   * The archive can filter on `replied` and nothing else, so a view that also
   * depends on the desk's own state narrows here instead. `DESK_VIEWS` holds
   * which part is which.
   */
  const shown = useMemo(
    () => conversations.filter((one) => {
      const entry = entries[one.id];
      if (!viewKeeps(view, stateOf(one), entry)) return false;
      // The reason filter is the Filed view's own second control and has no
      // meaning anywhere else, so it is not consulted anywhere else.
      return view.value !== FILED_VIEW || filingMatches(filing, entry?.triage);
    }),
    [conversations, entries, view, stateOf, filing],
  );

  /**
   * One action, run against the desk, with the entry it answers merged into
   * both the open conversation and the queue behind it — so a row stops
   * offering itself the moment it is taken.
   */
  /**
   * One entry, into the open conversation and the queue row behind it — so a
   * row stops offering itself the moment it is taken.
   */
  const paint = (entry) => {
    setOpen((was) => ({ ...was, entry }));
    setEntries((was) => ({ ...was, [selectedId]: entry }));
  };

  const act = async (run) => {
    setBusy(true);
    setDeskError('');
    try {
      paint(await run());
    } catch (err) {
      // A race, not a fault: somebody pressed Take a second earlier. The
      // refusal carries the CURRENT review, so the loser repaints from the
      // truth — merged onto the entry rather than replacing it, because a
      // refusal says nothing about the draft or the triage verdict and
      // blanking those would turn "somebody else has this" into "and the
      // draft is gone".
      //
      // THE SERVER'S OWN SENTENCE, which names the colleague who has it. A
      // generic one here would be a second copy of a message the API already
      // writes better, and the second person needs the name.
      if (isHeldByAnother(err)) {
        const review = reviewFromError(err);
        if (review) paint(withReview(open.entry, review));
        setDeskError(deskErrorText(err, 'Somebody else got there first.'));
      } else {
        setDeskError(deskErrorText(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const onSearch = (event) => {
    event.preventDefault();
    setSearch(searchInput);
  };

  const who = thread ? participantName(thread) : '';

  /**
   * THE OPEN CONVERSATION'S ENTRY, WITH THE TRIAGE VERDICT THE QUEUE HOLDS.
   *
   * The single-conversation read is addressed by one conversation and answers
   * about the review, the draft and the run; the funnel's verdict comes off the
   * window read, which this page already has in `entries`. Composing the two
   * levels here is what the desk service's header describes — and it is why
   * `GET /ai/drafts?conversation_id=` does not pay for a fourth query to serve
   * a field the Gmail add-on, its other caller, never draws.
   *
   * WITHOUT IT A FILED CONVERSATION WOULD OPEN AS "Needs a reply": the badge
   * in the queue and the badge in the header are the same function, and one of
   * them reading `triage: null` would make the two disagree.
   */
  const openEntry = useMemo(() => (
    open.entry && { ...open.entry, triage: entries[selectedId]?.triage ?? null }
  ), [open.entry, entries, selectedId]);

  const openState = thread ? deskState(thread, openEntry, me) : null;

  return (
    <Container fluid className="cs-page">
      <div className="cs-filters">
        <ButtonGroup size="sm" className="cs-filters__segments">
          {DESK_VIEWS.map((one) => (
            <Button
              key={one.value}
              variant={one.value === view.value ? 'primary' : 'outline-secondary'}
              onClick={() => setViewKey(one.value)}
              data-testid={`cs-view-${one.value}`}
            >
              {one.label}
              {one.value === view.value && shown.length > 0 && (
                <span className="cs-filters__count">
                  {shown.length}
                  {cursor ? '+' : ''}
                </span>
              )}
            </Button>
          ))}
        </ButtonGroup>

        {/* The Filed view's own second control. Shown only there, because a
            reason filter over conversations that were never refused is a
            control that can only ever empty the list. */}
        {view.value === FILED_VIEW && (
          <Form.Select
            size="sm"
            className="cs-filters__reason"
            value={filing}
            aria-label="Filed for which reason"
            onChange={(event) => setFiling(event.target.value)}
          >
            {FILING_FILTERS.map((one) => (
              <option key={one.value || 'any'} value={one.value}>{one.label}</option>
            ))}
          </Form.Select>
        )}

        <Form className="cs-filters__search" onSubmit={onSearch}>
          <Form.Control
            size="sm"
            type="search"
            placeholder="Sender or subject…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            aria-label="Search sender or subject"
          />
        </Form>

        <div className="cs-filters__dates">
          <Form.Control
            size="sm"
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            aria-label="From date"
          />
          <span className="text-muted">–</span>
          <Form.Control
            size="sm"
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            aria-label="To date"
          />
        </div>
      </div>

      {search && (
        <div className="cs-filters__hint text-muted">
          Matching
          {' '}
          {searchHint(search)}
        </div>
      )}

      {/* WHY THE DEFAULT VIEW CAN LOOK EMPTY, said where it is being read
          rather than left for somebody to assume the filter is broken.
          Triage is new and is deliberately never run backwards, so almost
          none of the archive carries a verdict — those conversations are
          under `Not triaged`, not lost, and they are not claimed to be
          either eligible or refused. */}
      {triageEpoch && view.triage === 'eligible' && (
        <Alert
          variant="secondary"
          className="cs-resolution py-1 px-2 small"
          data-testid="cs-epoch"
        >
          This view is mail triage judged worth a person&apos;s time. Nothing
          that arrived before
          {' '}
          {String(triageEpoch).slice(0, 10)}
          {' '}
          was ever triaged — that mail is under Not triaged, and the archive
          there is unfiltered because nobody looked at it, not because this
          filter failed.
        </Alert>
      )}

      {view.triage === 'none' && (
        <Alert variant="secondary" className="cs-resolution py-1 px-2 small">
          No verdict was ever recorded for these. They are not refused and not
          cleared — the gates and the model simply never ran on them.
        </Alert>
      )}

      {/*
        What the replied states actually cover. The server resolves them with a
        bounded backward pass, and past it a conversation reports `unknown`
        rather than a guess — so a filter that silently stopped being exact
        partway down the list would be worse than one that says where it stops.
      */}
      {resolution?.truncated && resolution.from && (
        <Alert variant="secondary" className="cs-resolution py-1 px-2 small">
          Replied states are exact back to
          {' '}
          {String(resolution.from).slice(0, 10)}
          . Older conversations below that read as unknown.
        </Alert>
      )}

      {/* The desk's own cap. A row whose entry was left behind would draw as
          unclaimed, which is a wrong answer rather than a missing one. */}
      {deskTruncated && (
        <Alert variant="secondary" className="cs-resolution py-1 px-2 small">
          More desk activity exists in this range than one page holds. Narrow
          the dates to be sure of who holds the older conversations.
        </Alert>
      )}

      {error && <Alert variant="danger" className="py-1 px-2 small">{error}</Alert>}

      {/* The desk failed for a reason that is not "it is not built yet". The
          queue below is therefore the archive's reading alone, and saying so
          matters more than usual: an unsaid one draws held conversations as
          free. */}
      {deskFailure && (
        <Alert variant="danger" className="py-1 px-2 small" data-testid="cs-desk-failure">
          {deskFailure}
          {' '}
          Nobody’s claim or verdict is visible until that read succeeds.
        </Alert>
      )}

      <div className="cs-panes">
        <div className="cs-panes__queue">
          <ConversationList
            conversations={shown}
            stateOf={stateOf}
            selectedId={selectedId}
            onSelect={setSelectedId}
            /* Both halves, because a queue painted before the desk answers
               shows held conversations as free for as long as it takes. */
            loading={listLoading || deskLoading}
            cursor={cursor}
            loadingMore={loadingMore}
            onLoadMore={() => load({ append: true, from: cursor })}
          />
        </div>
        <div className="cs-panes__thread">
          <ConversationThread
            conversation={thread}
            state={openState}
            entry={openEntry}
            draft={open.draft}
            run={open.run}
            loading={threadLoading}
            error={threadError}
            busy={busy}
            deskError={deskError}
            onClaim={() => act(() => DeskService.claim(selectedId))}
            onRelease={() => act(() => DeskService.release(selectedId))}
            /* ONE WRITE FOR ALL FIVE CONTROLS, because all five are the
               same decision in the server's vocabulary: which of the four
               `DRAFT_RESOLUTIONS` this conversation came to. The draft card
               names two of them and the holder controls name the other two. */
            onResolve={(outcome, sentText) => act(
              () => DeskService.resolve(selectedId, outcome, sentText),
            )}
          />
        </div>
        {/* The third column exists only once there is something to put in
            it; an empty bordered box beside an empty thread is furniture. */}
        {!!thread && (
          <div className="cs-panes__context">
            <ContextPanel
              conversation={thread}
              reference={conversationBookingReference(thread)}
              who={who}
              run={open.run}
              onSelectConversation={setSelectedId}
            />
          </div>
        )}
      </div>
    </Container>
  );
}

export default CustomerService;
